import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../lib/AuthContext";
import { ListPlus, Check, CircleNotch, Crown } from "@phosphor-icons/react";
import { Button } from "./ui/button";

const AddToWatchlistBtn = ({ npi, providerName, providerType, specialty }) => {
  const { user, isAuthenticated, loading: authLoading, login, authFetch } = useAuth();
  const [inWatchlist, setInWatchlist] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);

  const checkStatus = useCallback(async () => {
    if (!user || !npi) { setChecking(false); return; }
    try {
      const res = await authFetch(`${window.location.origin}/api/watchlist/check/${npi}`);
      if (!res.ok) throw new Error();
      const data = await res.json();
      setInWatchlist(data.in_watchlist);
    } catch { /* ignore */ } finally { setChecking(false); }
  }, [user, npi, authFetch]);

  useEffect(() => { checkStatus(); }, [checkStatus]);

  // Loading state
  if (authLoading) return null;

  // Not logged in
  if (!isAuthenticated) {
    return (
      <Button onClick={() => login({ appState: { returnTo: window.location.pathname } })}
        variant="outline" size="sm" className="gap-2 rounded-lg text-sm border-slate-300" data-testid="watchlist-login-btn">
        <ListPlus className="w-4 h-4" weight="bold" /> Watch
      </Button>
    );
  }

  // Logged in but profile loading — show disabled state
  if (!user) {
    return (
      <Button variant="outline" size="sm" className="gap-2 rounded-lg text-sm border-slate-300" disabled>
        <CircleNotch className="w-4 h-4 animate-spin" /> Watch
      </Button>
    );
  }

  // Already in watchlist
  if (inWatchlist) {
    return (
      <Button variant="outline" size="sm"
        className="gap-2 rounded-lg text-sm border-teal/30 bg-teal/10 text-teal" data-testid="watchlist-added-btn" disabled>
        <Check className="w-4 h-4" weight="bold" /> Watching
      </Button>
    );
  }

  const handleAdd = async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${window.location.origin}/api/watchlist/add`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ npi, provider_name: providerName, provider_type: providerType, specialty }),
      });
      if (res.ok) setInWatchlist(true);
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  return (
    <Button onClick={handleAdd} disabled={loading || checking}
      variant="outline" size="sm" className="gap-2 rounded-lg text-sm border-slate-300 hover:border-teal hover:text-teal" data-testid="watchlist-add-btn">
      {loading ? <CircleNotch className="w-4 h-4 animate-spin" /> : <ListPlus className="w-4 h-4" weight="bold" />} Watch
    </Button>
  );
};

export default AddToWatchlistBtn;
